/**
 * Sacar la foto del DNI con la cámara, sin salir de la página.
 *
 * Existe porque "Elegir foto" obliga a tener la foto sacada de antes: en el
 * celular manda a la galería y en la computadora al explorador de archivos.
 * Acá se ve la cámara en vivo y se captura, que es lo que hace falta cuando el
 * documento está sobre la mesa.
 *
 * Entrega un File igual que el selector de archivos, así lo que sigue —el
 * OCR, la validación, la redacción— no se entera de por dónde entró la foto.
 *
 * Se pide la cámara de atrás y la mayor resolución que el equipo dé: la banda
 * de lectura del dorso es letra chica y el OCR vive de esos píxeles. Lo que
 * venga más grande que el tope de trabajo se achica después, en fileToCanvas.
 *
 * En el celular (KAN-464, encuadre.ts) el diálogo va a pantalla completa, se ve
 * solo el marco con un margen chico, y el marco va a lo largo del lado largo del
 * video: con la pantalla vertical, girado 90°, con el aviso de poner el celular
 * horizontal y los mensajes de la guía girados con él. La foto se entrega
 * derecha, con la tarjeta apaisada: la del cuadro entero, girada.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { CheckCircleIcon } from '../../../icons';
import { marcoDeLaTarjeta } from './reader/marcoDeLaTarjeta';
import type { Rectangulo } from './reader/types';
import {
  dibujarDerecho,
  enPorcentajes,
  esCelular,
  esquinaDeLaTarjetaEn,
  giroDelMarco,
  marcoEnElVideo,
  medidasDerechas,
  tamanoQueEntra,
  vistaDelMarco,
} from './encuadre';
import type { GiroDelMarco } from './encuadre';
import type { CaraDeLaGuia } from './guiaEnVivo/bandaDeLectura';
import type { MensajeDeLaGuia } from './guiaEnVivo/guia';
import { useGuiaEnVivo } from './guiaEnVivo/useGuiaEnVivo';
import type { DependenciasDeLaGuia } from './guiaEnVivo/useGuiaEnVivo';

const DEFAULT_LANG = (process.env.REACT_APP_DEFAULT_LANGUAGE || 'es') as string;

/** Los textos de la cámara, en los tres idiomas; se exportan para la prueba del trato. */
export const TEXTOS: Record<string, Record<string, string>> = {
  es: {
    abriendo: 'Abriendo la cámara…',
    capturar: 'Capturar',
    cancelar: 'Cancelar',
    marco: 'El DNI, dentro del marco',
    acercar: 'Más cerca',
    fondo: 'Mejor sobre una superficie oscura',
    alejar: 'Un poco más lejos',
    enderezar: 'Está torcido',
    reflejo: 'Hay un reflejo: mejor un poco inclinado',
    quieto: 'Sin mover el documento…',
    listo: '¡Listo!',
    horizontal: 'Poné el celular horizontal',
    ayuda: 'Apoyá el documento sobre una superficie plana, sin reflejos, y que entre entero en el recuadro.',
    sin_permiso: 'El navegador no nos deja usar la cámara. Revisá tres cosas: que este sitio la tenga permitida (tocando el candado al lado de la dirección), que el navegador tenga permiso de cámara en los ajustes del celular, y que hayas abierto la página en el navegador y no dentro de otra aplicación. Si ya dijiste que no una vez, el navegador no vuelve a preguntar solo.',
    sin_camara: 'No encontramos una cámara disponible. Puede estar en uso por otra aplicación: cerrala y volvé a intentar, o usá "Elegir foto".',
    sin_soporte: 'Este navegador no permite usar la cámara desde la página. Usá "Elegir foto".',
    sin_https: 'El navegador solo habilita la cámara en páginas seguras (https), y esta página se abrió por http. Entrá por la dirección https, o usá "Elegir foto" — que en el celular también deja sacar la foto en el momento.',
    error: 'No se pudo abrir la cámara. Usá "Elegir foto".',
  },
  en: {
    abriendo: 'Opening the camera…',
    capturar: 'Capture',
    cancelar: 'Cancel',
    marco: 'The ID, inside the frame',
    acercar: 'Closer',
    fondo: 'Better on a dark surface',
    alejar: 'A little farther away',
    enderezar: 'Straighten it',
    reflejo: 'Glare: tilt the document slightly',
    quieto: 'Hold still…',
    listo: 'Done!',
    horizontal: 'Turn your phone sideways',
    ayuda: 'Place the document on a flat surface, avoiding glare, and make sure it fits entirely in the frame.',
    sin_permiso: 'The browser will not let us use the camera. Check three things: that this site is allowed to use it (tap the padlock next to the address), that the browser itself has camera permission in the phone settings, and that you opened the page in the browser and not inside another app. Once you have said no, it will not ask again on its own.',
    sin_camara: 'No camera found on this device. Please use "Choose photo".',
    sin_soporte: 'This browser cannot use the camera from the page. Please use "Choose photo".',
    sin_https: 'Browsers only allow the camera on secure (https) pages, and this page was opened over http. Use the https address, or use "Choose photo" — on a phone that also lets you take the photo right away.',
    error: 'Could not open the camera. Please use "Choose photo".',
  },
  pt: {
    abriendo: 'Abrindo a câmera…',
    capturar: 'Capturar',
    cancelar: 'Cancelar',
    marco: 'O documento, dentro da moldura',
    acercar: 'Mais perto',
    fondo: 'Melhor sobre uma superfície escura',
    alejar: 'Um pouco mais longe',
    enderezar: 'Está torto',
    reflejo: 'Há um reflexo: melhor um pouco inclinado',
    quieto: 'Sem mover o documento…',
    listo: 'Pronto!',
    horizontal: 'Vire o celular na horizontal',
    ayuda: 'Apoie o documento numa superfície plana, sem reflexos, e que caiba inteiro no quadro.',
    sin_permiso: 'O navegador não nos deixa usar a câmera. Verifique três coisas: que este site tenha permissão (toque no cadeado ao lado do endereço), que o navegador tenha permissão de câmera nas configurações do telefone, e que tenha aberto a página no navegador e não dentro de outro aplicativo. Depois de negar uma vez, não pergunta sozinho.',
    sin_camara: 'Não encontramos uma câmera neste dispositivo. Use "Escolher foto".',
    sin_soporte: 'Este navegador não permite usar a câmera pela página. Use "Escolher foto".',
    sin_https: 'O navegador só habilita a câmera em páginas seguras (https), e esta foi aberta por http. Use o endereço https, ou "Escolher foto" — no celular também permite tirar a foto na hora.',
    error: 'Não foi possível abrir a câmera. Use "Escolher foto".',
  },
};
const T = TEXTOS[DEFAULT_LANG] ?? TEXTOS.es;

export interface CamaraDialogProps {
  open: boolean;
  onClose: () => void;
  titulo: string;
  /**
   * La foto capturada, como si viniera del selector de archivos, y el marco en fracciones de la
   * foto: una pista de dónde está la tarjeta para la lectura, nunca una garantía.
   */
  onCaptura: (foto: File, pista?: Rectangulo) => void;
  /**
   * La cara que se saca: con ella corre la guía en vivo (las esquinas, el mensaje y la foto que
   * se saca sola). Sin ella, el marco estático.
   */
  cara?: CaraDeLaGuia;
  /** Para las pruebas: reemplaza lo que la guía toma del navegador (un video simulado). */
  dependenciasDeLaGuia?: Partial<DependenciasDeLaGuia>;
  /** Para las pruebas: si es un celular (si no, se pregunta al navegador, encuadre.ts). */
  celular?: boolean;
}

/** El texto de cada mensaje de la guía; «ubicar» es el del marco. */
const TEXTO_DEL_MENSAJE: Record<MensajeDeLaGuia, keyof typeof TEXTOS.es> = {
  ubicar: 'marco',
  acercar: 'acercar',
  fondo: 'fondo',
  alejar: 'alejar',
  enderezar: 'enderezar',
  reflejo: 'reflejo',
  quieto: 'quieto',
  listo: 'listo',
};

/** Una esquina del marco: dos lados de una L, que se encienden en verde. */
const ESQUINAS = [
  { top: 0, left: 0, borderTop: 1, borderLeft: 1 },
  { top: 0, right: 0, borderTop: 1, borderRight: 1 },
  { bottom: 0, right: 0, borderBottom: 1, borderRight: 1 },
  { bottom: 0, left: 0, borderBottom: 1, borderLeft: 1 },
] as const;
const VERDE = '#2e7d32';

/**
 * El marco de la tarjeta (marcoDeLaTarjeta) en fracciones de una foto de ese tamaño. La foto que
 * se entrega va siempre derecha (con el marco girado, se gira al sacarla): el marco es el apaisado.
 */
export function pistaDelMarco(ancho: number, alto: number): Rectangulo {
  const m = marcoDeLaTarjeta(ancho, alto);
  return { x: m.x / ancho, y: m.y / alto, w: m.w / ancho, h: m.h / alto };
}

/** El lugar disponible para la vista en el celular, medido en pantalla; null hasta medirlo. */
function useTamanoDe(elemento: HTMLElement | null): { ancho: number; alto: number } | null {
  const [tamano, setTamano] = useState<{ ancho: number; alto: number } | null>(null);
  useEffect(() => {
    if (!elemento) return;
    const medir = () => {
      const r = elemento.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setTamano((t) => (t && t.ancho === r.width && t.alto === r.height ? t : { ancho: r.width, alto: r.height }));
      }
    };
    medir();
    const observador = typeof ResizeObserver === 'function' ? new ResizeObserver(medir) : null;
    observador?.observe(elemento);
    window.addEventListener('resize', medir);
    window.addEventListener('orientationchange', medir);
    return () => {
      observador?.disconnect();
      window.removeEventListener('resize', medir);
      window.removeEventListener('orientationchange', medir);
    };
  }, [elemento]);
  return tamano;
}

export const CamaraDialog: React.FC<CamaraDialogProps> = ({
  open,
  onClose,
  titulo,
  onCaptura,
  cara,
  dependenciasDeLaGuia,
  celular: celularDado,
}) => {
  const [celular] = useState(() => celularDado ?? esCelular());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  /** El video, también como estado: la guía arranca cuando existe. */
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Si ya se está sacando la foto (el botón o la guía). */
  const capturando = useRef(false);
  const [lista, setLista] = useState(false);
  const [error, setError] = useState('');
  /** Las medidas del video, para dibujar el marco con la proporción de la tarjeta. */
  const [medidas, setMedidas] = useState<{
    ancho: number;
    alto: number;
  } | null>(null);
  /** El lugar para la vista en el celular. */
  const [area, setArea] = useState<HTMLDivElement | null>(null);
  const disponible = useTamanoDe(area);

  // Si la página gira con el celular, el video cambia de forma: el marco y la vista lo siguen.
  useEffect(() => {
    if (!video) return;
    const medir = () => {
      const ancho = video.videoWidth;
      const alto = video.videoHeight;
      if (!ancho || !alto) return;
      setMedidas((m) => (m && m.ancho === ancho && m.alto === alto ? m : { ancho, alto }));
    };
    video.addEventListener('resize', medir);
    return () => video.removeEventListener('resize', medir);
  }, [video]);

  useEffect(() => {
    if (!open) return;
    capturando.current = false;
    setLista(false);
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      // La causa casi siempre es el protocolo y no el navegador: fuera de un
      // contexto seguro el navegador ni siquiera expone la API, así que NO hay
      // permiso que dar ni diálogo que aparezca. Decir "este navegador no
      // puede" manda a la persona a buscar donde no está el problema.
      setError(window.isSecureContext === false ? T.sin_https : T.sin_soporte);
      return;
    }
    // Si se cierra el modal mientras el navegador todavía está pidiendo
    // permiso, la cámara igual queda prendida: por eso se apaga el stream que
    // llegue tarde en vez de dejarlo colgado.
    let cancelado = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 2560 },
            height: { ideal: 1440 },
          },
          audio: false,
        });
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // Donde el navegador lo permite (Android), el foco continuo: la guía lee letras.
        const pista = stream.getVideoTracks?.()[0];
        const capacidades = (pista?.getCapabilities?.() ?? {}) as { focusMode?: string[] };
        if (capacidades.focusMode?.includes('continuous')) {
          pista!
            .applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] })
            .catch(() => undefined);
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setLista(true);
      } catch (e) {
        const nombre = (e as Error).name;
        setError(
          nombre === 'NotAllowedError' || nombre === 'SecurityError'
            ? T.sin_permiso
            : nombre === 'NotFoundError' ||
              nombre === 'NotReadableError' ||
              nombre === 'OverconstrainedError'
            ? T.sin_camara
            : T.error
        );
      }
    })();
    return () => {
      cancelado = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);

  const capturar = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    // Una sola vez: el botón y la guía pueden coincidir.
    if (capturando.current) return;
    capturando.current = true;
    const canvas = document.createElement('canvas');
    // Con el marco girado, la foto se saca girada: derecha, con la tarjeta apaisada, como con el
    // celular horizontal. Así la lectura la recibe como está calibrada y la pista es el marco de siempre.
    const giro = giroDelMarco(video.videoWidth, video.videoHeight, celular);
    const derecha = medidasDerechas(video.videoWidth, video.videoHeight, giro);
    canvas.width = derecha.ancho;
    canvas.height = derecha.alto;
    const ctx = canvas.getContext('2d')!;
    if (giro === 0) ctx.drawImage(video, 0, 0);
    else dibujarDerecho(ctx, video, video.videoWidth, { x: 0, y: 0, w: derecha.ancho, h: derecha.alto }, derecha, giro);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          capturando.current = false;
          return;
        }
        // La foto se saca entera y a resolución completa; el marco viaja como pista.
        onCaptura(new File([blob], 'dni.jpg', { type: 'image/jpeg' }), pistaDelMarco(canvas.width, canvas.height));
        onClose();
      },
      'image/jpeg',
      0.92
    );
  };

  // La guía en vivo: con el video andando y la cara que se saca. Saca la foto sola por el mismo
  // camino que el botón.
  const guia = useGuiaEnVivo({
    video,
    activa: open && lista && !error,
    cara,
    celular,
    onCapturar: capturar,
    dependencias: dependenciasDeLaGuia,
  });
  const estado = guia.estado;
  const mensaje = estado ? T[TEXTO_DEL_MENSAJE[estado.mensaje]] : T.marco;

  const giro: GiroDelMarco = medidas ? giroDelMarco(medidas.ancho, medidas.alto, celular) : 0;
  const refDelVideo = (v: HTMLVideoElement | null) => {
    videoRef.current = v;
    setVideo(v);
  };
  const alCargar = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) setMedidas({ ancho: v.videoWidth, alto: v.videoHeight });
  };

  /**
   * La silueta de la tarjeta sobre el video, decorativa: afuera, levemente oscurecido. `ventana` es
   * la parte del video que se ve (todo, en la computadora; el marco y su margen, en el celular).
   * En el celular, encima va además el mensaje de la guía, girado con el marco (en su propia capa:
   * la silueta queda oculta al lector de pantalla y el mensaje no).
   */
  const marcoSobreElVideo = (ventana: Rectangulo, conMensaje: boolean) => {
    if (!medidas) return null;
    const m = marcoEnElVideo(medidas.ancho, medidas.alto, giro);
    const girado = giro === 90;
    const lugar = enPorcentajes(m, ventana);
    return (
      <>
        <Box
          data-testid="marco-de-la-tarjeta"
          aria-hidden="true"
          data-giro={giro}
          style={lugar}
          data-verde={estado?.verde ? 'true' : undefined}
          sx={{
            position: 'absolute',
            border: `2px solid ${estado?.verde ? VERDE : 'rgba(255,255,255,0.9)'}`,
            borderRadius: girado ? '6%/4%' : '4%/6%',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
            transition: 'border-color 150ms',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          {/* Las esquinas que coinciden con el borde del DNI, en verde. Con el marco girado, cada
            esquina de la tarjeta cae en otra esquina de la pantalla. */}
          {estado &&
            ESQUINAS.map((e, posicion) => {
              const i = esquinaDeLaTarjetaEn(posicion, giro);
              return (
                <Box
                  key={i}
                  data-testid={`esquina-${i}`}
                  data-encendida={estado.esquinas[i] ? 'true' : 'false'}
                  sx={{
                    position: 'absolute',
                    top: 'top' in e ? -2 : undefined,
                    bottom: 'bottom' in e ? -2 : undefined,
                    left: 'left' in e ? -2 : undefined,
                    right: 'right' in e ? -2 : undefined,
                    width: girado ? '22%' : '14%',
                    height: girado ? '14%' : '22%',
                    borderStyle: 'solid',
                    borderColor: estado.esquinas[i] ? VERDE : 'rgba(255,255,255,0.9)',
                    borderWidth: 0,
                    borderTopWidth: 'borderTop' in e ? 5 : 0,
                    borderBottomWidth: 'borderBottom' in e ? 5 : 0,
                    borderLeftWidth: 'borderLeft' in e ? 5 : 0,
                    borderRightWidth: 'borderRight' in e ? 5 : 0,
                    transition: 'border-color 150ms',
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none',
                    },
                  }}
                />
              );
            })}
          {estado?.tomada && (
            <Box
              data-testid="tilde-de-la-guia"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: VERDE,
                  bgcolor: '#fff',
                  borderRadius: '50%',
                  transform: girado ? 'rotate(90deg)' : undefined,
                }}
              />
            </Box>
          )}
        </Box>
        {conMensaje && (
          // El mensaje, del lado de arriba de la tarjeta: con el marco girado, a la derecha y
          // escrito de arriba abajo, para leerlo con el celular horizontal.
          <Box style={lugar} sx={{ position: 'absolute', pointerEvents: 'none' }}>
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ...(girado ? { top: 0, bottom: 0, right: 8 } : { left: 0, right: 0, top: 8 }),
              }}
            >
              <Typography
                role="status"
                aria-live="polite"
                variant="body2"
                data-testid="mensaje-de-la-guia"
                style={girado ? { writingMode: 'vertical-rl' } : undefined}
                sx={{
                  fontWeight: 500,
                  textAlign: 'center',
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.6)',
                  borderRadius: 1,
                  px: girado ? 0.5 : 1.5,
                  py: girado ? 1.5 : 0.5,
                  ...(girado ? { maxHeight: '90%' } : { maxWidth: '90%' }),
                }}
              >
                {mensaje}
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );
  };

  const abriendo = !lista && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mt: 1,
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={18} />
      <Typography variant="body2">{T.abriendo}</Typography>
    </Box>
  );

  /**
   * En el celular: a pantalla completa, solo el marco y su margen, tan grande como entre. El video
   * se corre y se agranda dentro de la vista (porcentajes de la vista), sin deformarlo.
   */
  const enElCelular = () => {
    const vista = medidas
      ? vistaDelMarco(marcoEnElVideo(medidas.ancho, medidas.alto, giro), medidas.ancho, medidas.alto)
      : null;
    const tamano = vista && disponible ? tamanoQueEntra(vista, disponible) : null;
    const estiloDeLaVista: CSSProperties = !vista
      ? { width: '100%' }
      : tamano && tamano.ancho > 0
        ? { width: tamano.ancho, height: tamano.alto }
        : { width: '100%', aspectRatio: `${vista.w} / ${vista.h}` };
    const estiloDelVideo: CSSProperties =
      vista && medidas
        ? {
            position: 'absolute',
            ...enPorcentajes({ x: 0, y: 0, w: medidas.ancho, h: medidas.alto }, vista),
            maxWidth: 'none',
            objectFit: 'fill',
            background: '#000',
          }
        : { width: '100%', background: '#000' };
    return (
      <>
        {giro === 90 && (
          <Alert severity="info" data-testid="aviso-horizontal" sx={{ mb: 1, py: 0 }}>
            {T.horizontal}
          </Alert>
        )}
        {giro === 90 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {T.ayuda}
          </Typography>
        )}
        <Box
          ref={setArea}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            data-testid="vista-del-marco"
            style={estiloDeLaVista}
            sx={{
              position: 'relative',
              lineHeight: 0,
              overflow: 'hidden',
              borderRadius: 1,
              bgcolor: '#000',
            }}
          >
            <video ref={refDelVideo} playsInline muted onLoadedMetadata={alCargar} style={estiloDelVideo} />
            {vista && marcoSobreElVideo(vista, true)}
          </Box>
        </Box>
        {abriendo}
      </>
    );
  };

  /** En la computadora, como siempre: el video entero, con el marco encima y el mensaje arriba. */
  const enLaComputadora = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {T.ayuda}
      </Typography>
      {/* Un solo mensaje por vez; la región lo anuncia cuando cambia, sin apuro. */}
      <Typography role="status" aria-live="polite" variant="body1" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>
        {mensaje}
      </Typography>
      <Box sx={{ position: 'relative', textAlign: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            lineHeight: 0,
            overflow: 'hidden',
            borderRadius: 1,
          }}
        >
          <video
            ref={refDelVideo}
            playsInline
            muted
            onLoadedMetadata={alCargar}
            style={{ width: '100%', borderRadius: 4, background: '#000' }}
          />
          {medidas && marcoSobreElVideo({ x: 0, y: 0, w: medidas.ancho, h: medidas.alto }, false)}
        </Box>
        {abriendo}
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={celular}>
      <DialogTitle sx={celular ? { py: 1 } : undefined}>{titulo}</DialogTitle>
      <DialogContent sx={celular ? { display: 'flex', flexDirection: 'column', px: 1, pb: 0 } : undefined}>
        {error ? <Alert severity="warning">{error}</Alert> : celular ? enElCelular() : enLaComputadora()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{T.cancelar}</Button>
        <Button variant="contained" onClick={capturar} disabled={!lista}>
          {T.capturar}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
